import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Avatar,
  Divider,
} from '@mui/material';
import { Add, Check, Close, Pause, PlayArrow, Cancel, EventNote, BeachAccess, LocalHospital, MonetizationOn, HolidayVillage } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { leaveAPI, employeesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Swal from 'sweetalert2';

const MotionCard = motion(Card);

const Leave = () => {
  const { user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [allLeaves, setAllLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [balance, setBalance] = useState(null);
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [showAdminApplyDialog, setShowAdminApplyDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    leave_type: 'casual',
    start_date: '',
    end_date: '',
    reason: '',
    is_half_day: false,
  });
  const [adminFormData, setAdminFormData] = useState({
    employee_id: '',
    leave_type: 'casual',
    start_date: '',
    end_date: '',
    reason: '',
    is_half_day: false,
    status: 'approved',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isAdmin = user?.role === 'admin' || user?.role === 'hr';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const requests = [leaveAPI.getMyLeaves(), leaveAPI.getBalance()];

      if (isAdmin) {
        requests.push(leaveAPI.getAll());
      }

      const results = await Promise.all(requests);
      if (results[0].data.success) setLeaves(results[0].data.data.leaves);
      if (results[1].data.success) setBalance(results[1].data.data.leave_balance);

      if (isAdmin) {
        if (results[2]?.data.success) {
          setAllLeaves(results[2].data.data.leaves);
          try {
            const empRes = await employeesAPI.getAll({ status: 'active' });
            if (empRes.data.success) {
              const empList = empRes.data.data.employees || empRes.data.data;
              setEmployees(empList);
            }
          } catch (err) {
            console.error('Error loading employees:', err);
            setError('Failed to load employees list');
          }
        }
      }
    } catch (error) {
      console.error('Error loading leaves:', error);
      setError('Failed to load leave data');
    }
  };

  const handleEmployeeFilter = (employeeId) => {
    setSelectedEmployee(employeeId);
  };

  const handleApplyLeave = async () => {
    setError('');
    if (!formData.start_date || !formData.end_date) {
      setError('Please select start and end dates');
      return;
    }
    try {
      const response = await leaveAPI.apply(formData);
      if (response.data.success) {
        setSuccess('Leave applied successfully');
        setShowApplyDialog(false);
        setFormData({
          leave_type: 'casual',
          start_date: '',
          end_date: '',
          reason: '',
          is_half_day: false,
        });
        loadData();
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to apply leave');
    }
  };

  const handleAdminApplyLeave = async () => {
    setError('');
    if (!adminFormData.employee_id) {
      setError('Please select an employee');
      return;
    }
    if (!adminFormData.start_date || !adminFormData.end_date) {
      setError('Please select start and end dates');
      return;
    }
    try {
      const response = await leaveAPI.createForEmployee(adminFormData);
      if (response.data.success) {
        setSuccess('Leave created successfully for employee');
        setShowAdminApplyDialog(false);
        setAdminFormData({
          employee_id: '',
          leave_type: 'casual',
          start_date: '',
          end_date: '',
          reason: '',
          is_half_day: false,
          status: 'approved',
        });
        loadData();
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create leave');
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'approved': return { bgcolor: 'rgba(76, 175, 80, 0.15)', color: '#66BB6A', border: '1px solid #66BB6A' };
      case 'pending': return { bgcolor: 'rgba(255, 152, 0, 0.15)', color: '#FFB74D', border: '1px solid #FFB74D' };
      case 'rejected': return { bgcolor: 'rgba(239, 83, 80, 0.15)', color: '#EF5350', border: '1px solid #EF5350' };
      case 'on_hold': return { bgcolor: 'rgba(66, 165, 245, 0.15)', color: '#64B5F6', border: '1px solid #64B5F6' };
      case 'cancellation_requested': return { bgcolor: 'rgba(255, 152, 0, 0.15)', color: '#FFB74D', border: '1px solid #FFB74D' };
      default: return { bgcolor: 'rgba(100, 100, 100, 0.15)', color: '#B0BEC5', border: '1px solid #B0BEC5' };
    }
  };

  const getLeaveIcon = (type) => {
    switch (type) {
      case 'casual': return <BeachAccess sx={{ color: '#D4AF37' }} />;
      case 'sick': return <LocalHospital sx={{ color: '#D4AF37' }} />;
      case 'paid': return <MonetizationOn sx={{ color: '#D4AF37' }} />;
      case 'unpaid': return <HolidayVillage sx={{ color: '#D4AF37' }} />;
      default: return <EventNote sx={{ color: '#D4AF37' }} />;
    }
  };

  const handleApprove = async (leaveId) => {
    try {
      await leaveAPI.approve(leaveId);
      setSuccess('Leave approved successfully');
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to approve leave');
    }
  };

  const handleReject = async (leaveId) => {
    const result = await Swal.fire({
      title: 'Reject Leave',
      text: 'Enter rejection reason:',
      input: 'text',
      inputPlaceholder: 'Rejection reason',
      showCancelButton: true,
      confirmButtonText: 'Reject',
      cancelButtonText: 'Cancel',
      background: '#0F2744',
      color: '#D4AF37',
      confirmButtonColor: '#D4AF37',
      cancelButtonColor: '#EF5350',
    });
    if (result.isConfirmed && result.value) {
      try {
        await leaveAPI.reject(leaveId, { reason: result.value });
        setSuccess('Leave rejected');
        loadData();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to reject leave');
      }
    }
  };

  const handleHold = async (leaveId) => {
    try {
      await leaveAPI.hold(leaveId);
      setSuccess('Leave put on hold');
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to hold leave');
    }
  };

  const handleReleaseHold = async (leaveId) => {
    try {
      await leaveAPI.releaseHold(leaveId);
      setSuccess('Leave released from hold');
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to release leave from hold');
    }
  };

  const handleCancelRequest = async (leaveId) => {
    const result = await Swal.fire({
      title: 'Request Leave Cancellation',
      text: 'Enter reason for cancellation request:',
      input: 'text',
      inputPlaceholder: 'Cancellation reason',
      showCancelButton: true,
      confirmButtonText: 'Submit Request',
      cancelButtonText: 'Cancel',
      background: '#0F2744',
      color: '#D4AF37',
      confirmButtonColor: '#D4AF37',
      cancelButtonColor: '#EF5350',
    });
    if (result.isConfirmed) {
      try {
        await leaveAPI.cancel(leaveId, result.value || 'Cancellation requested by employee');
        setSuccess('Cancellation request sent to admin/HR');
        loadData();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to submit cancellation request');
      }
    }
  };

  const handleApproveCancellation = async (leaveId) => {
    try {
      await leaveAPI.approveCancellation(leaveId);
      setSuccess('Leave cancellation approved');
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to approve cancellation');
    }
  };

  const handleRejectCancellation = async (leaveId) => {
    try {
      await leaveAPI.rejectCancellation(leaveId);
      setSuccess('Leave cancellation request rejected');
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reject cancellation');
    }
  };

  const getDisplayLeaves = () => {
    let displayLeaves = isAdmin ? allLeaves : leaves;
    
    if (isAdmin && selectedEmployee) {
      displayLeaves = displayLeaves.filter(l => l.employee_id === selectedEmployee);
    }
    
    if (!isAdmin) return displayLeaves;
    if (tabValue === 0) return displayLeaves.filter(l => l.status === 'pending');
    if (tabValue === 1) return displayLeaves.filter(l => l.status === 'approved');
    if (tabValue === 2) return displayLeaves.filter(l => l.status === 'rejected');
    if (tabValue === 3) return displayLeaves.filter(l => l.status === 'on_hold');
    if (tabValue === 4) return displayLeaves.filter(l => l.status === 'cancellation_requested');
    return displayLeaves;
  };

  const formatDate = (date) => new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <Box sx={{ 
      background: 'linear-gradient(135deg, #0A1929 0%, #0F2744 100%)', 
      minHeight: '100vh', 
      p: { xs: 2, sm: 3 } 
    }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#D4AF37', letterSpacing: 0.5 }}>
              Leave Management
            </Typography>
            <Typography variant="body2" sx={{ color: '#8AA87D', mt: 0.5 }}>
              Apply, track, and manage leave requests
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {isAdmin && (
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button 
                  variant="outlined" 
                  color="primary"
                  startIcon={<Add />} 
                  onClick={() => { setShowApplyDialog(true); setError(''); }}
                  sx={{ fontWeight: 600 }}
                >
                  Apply for My Leave
                </Button>
              </motion.div>
            )}
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<Add />} 
                onClick={() => isAdmin ? (() => { setShowAdminApplyDialog(true); setError(''); })() : (() => { setShowApplyDialog(true); setError(''); })()}
                sx={{ fontWeight: 700 }}
              >
                {isAdmin ? 'Add Leave for Employee' : 'Apply for Leave'}
              </Button>
            </motion.div>
          </Box>
        </Box>

        {/* Admin Filters */}
        {isAdmin && (
          <MotionCard
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            sx={{ mb: 3 }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel sx={{ color: '#8AA87D' }}>Filter by Employee</InputLabel>
                  <Select
                    value={selectedEmployee}
                    label="Filter by Employee"
                    onChange={(e) => handleEmployeeFilter(e.target.value)}
                  >
                    <MenuItem value="">All Employees</MenuItem>
                    {employees.map((emp) => (
                      <MenuItem key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tabs 
                  value={tabValue} 
                  onChange={(e, v) => setTabValue(v)} 
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    flexGrow: 1,
                    '& .MuiTab-root': { color: '#8AA87D', fontWeight: 600, textTransform: 'none' },
                    '& .Mui-selected': { color: '#D4AF37' },
                    '& .MuiTabs-indicator': { bgcolor: '#D4AF37', height: 3, borderRadius: '3px 3px 0 0' },
                  }}
                >
                  <Tab label={`Pending (${allLeaves.filter(l => l.status === 'pending').length})`} />
                  <Tab label={`Approved (${allLeaves.filter(l => l.status === 'approved').length})`} />
                  <Tab label={`Rejected (${allLeaves.filter(l => l.status === 'rejected').length})`} />
                  <Tab label={`On Hold (${allLeaves.filter(l => l.status === 'on_hold').length})`} />
                  <Tab label={`Cancel Request (${allLeaves.filter(l => l.status === 'cancellation_requested').length})`} />
                </Tabs>
              </Box>
            </CardContent>
          </MotionCard>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Leave Balance Cards */}
        {balance && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {Object.entries(balance).map(([type, data]) => (
              <Grid item xs={12} sm={6} md={3} key={type}>
                <MotionCard
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ type: 'spring', stiffness: 120 }}
                  whileHover={{ y: -4 }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      {getLeaveIcon(type)}
                      <Typography variant="h6" sx={{ textTransform: 'capitalize', color: '#D4AF37', fontWeight: 700 }}>
                        {type} Leave
                      </Typography>
                    </Box>
                    {type === 'unpaid' ? (
                      <>
                        <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFFFFF' }}>{data.used}</Typography>
                        <Typography variant="body2" sx={{ color: '#8AA87D' }}>days used this year</Typography>
                        <Typography variant="body2" sx={{ color: '#66BB6A', mt: 1, fontWeight: 600 }}>Unlimited</Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="h3" sx={{ fontWeight: 800, color: '#D4AF37' }}>{data.available}</Typography>
                        <Typography variant="body2" sx={{ color: '#8AA87D' }}>of {data.total} days available</Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={data.total > 0 ? (data.available / data.total) * 100 : 100} 
                          sx={{ mt: 2, height: 8 }} 
                        />
                      </>
                    )}
                  </CardContent>
                </MotionCard>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Leave History Table */}
        <MotionCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#D4AF37', letterSpacing: 0.5 }}>Leave History</Typography>
            <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {isAdmin && <TableCell>Employee</TableCell>}
                    <TableCell>Type</TableCell>
                    <TableCell>Dates</TableCell>
                    <TableCell>Days</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getDisplayLeaves().map((leave) => (
                    <TableRow key={leave.id} hover>
                      {isAdmin && (
                        <TableCell sx={{ color: '#FFFFFF' }}>
                          {leave.leave_employee ? `${leave.leave_employee.first_name || ''} ${leave.leave_employee.last_name || ''}`.trim() : (leave.employee_name || '-')}
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getLeaveIcon(leave.leave_type)}
                          <Typography sx={{ textTransform: 'capitalize', color: '#FFFFFF', fontWeight: 500 }}>{leave.leave_type}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: '#B0BEC5' }}>{formatDate(leave.start_date)} - {formatDate(leave.end_date)}</TableCell>
                      <TableCell sx={{ color: '#D4AF37', fontWeight: 700 }}>{leave.total_days}</TableCell>
                      <TableCell>
                        <Chip 
                          size="small" 
                          label={leave.status === 'cancellation_requested' ? 'Cancel Request' : leave.status} 
                          sx={{ ...getStatusStyle(leave.status), fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#B0BEC5', maxWidth: 200 }}>{leave.reason || '-'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {!isAdmin && leave.status === 'approved' && (
                            <IconButton 
                              size="small" 
                              onClick={() => handleCancelRequest(leave.id)} 
                              title="Request Cancellation"
                              sx={{ color: '#EF5350', '&:hover': { bgcolor: 'rgba(239, 83, 80, 0.1)' } }}
                            >
                              <Cancel />
                            </IconButton>
                          )}
                          {isAdmin && (
                            <>
                              {leave.status === 'pending' && (
                                <>
                                  <IconButton size="small" onClick={() => handleApprove(leave.id)} title="Approve" sx={{ color: '#66BB6A', '&:hover': { bgcolor: 'rgba(102, 187, 106, 0.1)' } }}>
                                    <Check />
                                  </IconButton>
                                  <IconButton size="small" onClick={() => handleReject(leave.id)} title="Reject" sx={{ color: '#EF5350', '&:hover': { bgcolor: 'rgba(239, 83, 80, 0.1)' } }}>
                                    <Close />
                                  </IconButton>
                                  <IconButton size="small" onClick={() => handleHold(leave.id)} title="Hold" sx={{ color: '#64B5F6', '&:hover': { bgcolor: 'rgba(100, 181, 246, 0.1)' } }}>
                                    <Pause />
                                  </IconButton>
                                </>
                              )}
                              {leave.status === 'on_hold' && (
                                <>
                                  <IconButton size="small" onClick={() => handleApprove(leave.id)} title="Approve" sx={{ color: '#66BB6A', '&:hover': { bgcolor: 'rgba(102, 187, 106, 0.1)' } }}>
                                    <Check />
                                  </IconButton>
                                  <IconButton size="small" onClick={() => handleReject(leave.id)} title="Reject" sx={{ color: '#EF5350', '&:hover': { bgcolor: 'rgba(239, 83, 80, 0.1)' } }}>
                                    <Close />
                                  </IconButton>
                                  <IconButton size="small" onClick={() => handleReleaseHold(leave.id)} title="Release Hold" sx={{ color: '#D4AF37', '&:hover': { bgcolor: 'rgba(212, 175, 55, 0.1)' } }}>
                                    <PlayArrow />
                                  </IconButton>
                                </>
                              )}
                              {leave.status === 'cancellation_requested' && (
                                <>
                                  <IconButton size="small" onClick={() => handleApproveCancellation(leave.id)} title="Approve Cancellation" sx={{ color: '#66BB6A', '&:hover': { bgcolor: 'rgba(102, 187, 106, 0.1)' } }}>
                                    <Check />
                                  </IconButton>
                                  <IconButton size="small" onClick={() => handleRejectCancellation(leave.id)} title="Reject Cancellation" sx={{ color: '#EF5350', '&:hover': { bgcolor: 'rgba(239, 83, 80, 0.1)' } }}>
                                    <Close />
                                  </IconButton>
                                </>
                              )}
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {getDisplayLeaves().length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 7 : 6} sx={{ textAlign: 'center', py: 4, color: '#8AA87D' }}>
                        No leave records found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </MotionCard>

        {/* Apply Dialog */}
        <Dialog open={showApplyDialog} onClose={() => setShowApplyDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ color: '#D4AF37', fontWeight: 700, borderBottom: '1px solid rgba(212,175,55,0.1)' }}>
            Apply for Leave
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <TextField 
              select 
              fullWidth 
              label="Leave Type" 
              value={formData.leave_type} 
              onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })} 
              sx={{ mb: 2.5 }}
            >
              <MenuItem value="casual">Casual Leave</MenuItem>
              <MenuItem value="sick">Sick Leave</MenuItem>
              <MenuItem value="paid">Paid Leave</MenuItem>
              <MenuItem value="unpaid">Unpaid Leave</MenuItem>
            </TextField>
            <TextField 
              fullWidth 
              label="Start Date" 
              type="date" 
              value={formData.start_date} 
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} 
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2.5 }}
            />
            <TextField 
              fullWidth 
              label="End Date" 
              type="date" 
              value={formData.end_date} 
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} 
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2.5 }}
            />
            <TextField 
              fullWidth 
              label="Reason" 
              multiline 
              rows={3} 
              value={formData.reason} 
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })} 
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_half_day}
                  onChange={(e) => setFormData({ ...formData, is_half_day: e.target.checked })}
                  sx={{ color: '#D4AF37', '&.Mui-checked': { color: '#D4AF37' } }}
                />
              }
              label="Half Day Leave"
              sx={{ color: '#8AA87D' }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(212,175,55,0.1)' }}>
            <Button onClick={() => setShowApplyDialog(false)} sx={{ color: '#B0BEC5', fontWeight: 600 }}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleApplyLeave} 
              disabled={!formData.start_date || !formData.end_date}
              sx={{ fontWeight: 700 }}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>

        {/* Admin: Add Leave for Employee Dialog */}
        <Dialog open={showAdminApplyDialog} onClose={() => setShowAdminApplyDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ color: '#D4AF37', fontWeight: 700, borderBottom: '1px solid rgba(212,175,55,0.1)' }}>
            Add Leave for Employee
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <FormControl fullWidth sx={{ mb: 2.5 }}>
              <InputLabel>Select Employee</InputLabel>
              <Select
                value={adminFormData.employee_id}
                label="Select Employee"
                onChange={(e) => setAdminFormData({ ...adminFormData, employee_id: e.target.value })}
              >
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name} ({emp.employee_id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField 
              select 
              fullWidth 
              label="Leave Type" 
              value={adminFormData.leave_type} 
              onChange={(e) => setAdminFormData({ ...adminFormData, leave_type: e.target.value })} 
              sx={{ mb: 2.5 }}
            >
              <MenuItem value="casual">Casual Leave</MenuItem>
              <MenuItem value="sick">Sick Leave</MenuItem>
              <MenuItem value="paid">Paid Leave</MenuItem>
              <MenuItem value="unpaid">Unpaid Leave</MenuItem>
            </TextField>
            <TextField 
              fullWidth 
              label="Start Date" 
              type="date" 
              value={adminFormData.start_date} 
              onChange={(e) => setAdminFormData({ ...adminFormData, start_date: e.target.value })} 
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2.5 }}
            />
            <TextField 
              fullWidth 
              label="End Date" 
              type="date" 
              value={adminFormData.end_date} 
              onChange={(e) => setAdminFormData({ ...adminFormData, end_date: e.target.value })} 
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2.5 }}
            />
            <TextField 
              select 
              fullWidth 
              label="Status" 
              value={adminFormData.status} 
              onChange={(e) => setAdminFormData({ ...adminFormData, status: e.target.value })} 
              sx={{ mb: 2.5 }}
            >
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </TextField>
            <TextField 
              fullWidth 
              label="Reason" 
              multiline 
              rows={3} 
              value={adminFormData.reason} 
              onChange={(e) => setAdminFormData({ ...adminFormData, reason: e.target.value })} 
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={adminFormData.is_half_day}
                  onChange={(e) => setAdminFormData({ ...adminFormData, is_half_day: e.target.checked })}
                  sx={{ color: '#D4AF37', '&.Mui-checked': { color: '#D4AF37' } }}
                />
              }
              label="Half Day Leave"
              sx={{ color: '#8AA87D' }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(212,175,55,0.1)' }}>
            <Button onClick={() => setShowAdminApplyDialog(false)} sx={{ color: '#B0BEC5', fontWeight: 600 }}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleAdminApplyLeave} 
              disabled={!adminFormData.employee_id || !adminFormData.start_date || !adminFormData.end_date}
              sx={{ fontWeight: 700 }}
            >
              Create Leave
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Leave;